import { themesApi } from '../../api/client';
import { themeStyles, getThemeStyles } from '../../styles/themes';
import { extractColorFromClass, colorProperties } from './colorUtils';
import type { ThemeManagerState } from './useThemeManagerState';

interface HandlerDependencies {
  state: ThemeManagerState;
}

export const createThemeManagerHandlers = ({ state }: HandlerDependencies) => {
  const loadThemes = async () => {
    try {
      state.setIsLoading(true);
      const response = await themesApi.list();

      // Handle both array and object with themes property
      const themes = Array.isArray(response) ? response : (response as any).themes || [];

      state.setAvailableThemes(themes);
      if (themes.length > 0 && !state.selectedTheme) {
        state.setSelectedTheme(themes[0].id);
      }
    } catch (error) {
      console.error('Failed to load themes:', error);
      state.setError('Failed to load themes. Using local themes.');
      // Fallback to local themes
      const localThemes = Object.keys(themeStyles).map(id => ({
        id,
        name: id.charAt(0).toUpperCase() + id.slice(1),
        isCustom: false,
        customColors: null,
      }));
      state.setAvailableThemes(localThemes);
    } finally {
      state.setIsLoading(false);
    }
  };

  const extractColorsFromTheme = (themeId: string): Record<string, Record<string, string>> => {
    const currentTheme = getThemeStyles(themeId);
    const extractedColors: Record<string, Record<string, string>> = {};

    Object.entries(currentTheme).forEach(([componentKey, styles]) => {
      extractedColors[componentKey] = {};
      const allowedProps = colorProperties[componentKey] || [];

      allowedProps.forEach(prop => {
        if (styles[prop as keyof typeof styles]) {
          const value = styles[prop as keyof typeof styles] as string;
          if (value.startsWith('#')) {
            extractedColors[componentKey][prop] = value;
          } else {
            extractedColors[componentKey][prop] = extractColorFromClass(value);
          }
        }
      });
    });

    return extractedColors;
  };

  const loadThemeDetails = async () => {
    try {
      const themeData = await themesApi.get(state.selectedTheme);

      if (themeData.customColors) {
        state.setCustomColors(themeData.customColors);
      } else {
        state.setCustomColors(extractColorsFromTheme(state.selectedTheme));
      }
    } catch (error) {
      console.error('Failed to load theme details:', error);
      state.setCustomColors(extractColorsFromTheme(state.selectedTheme));
    }
  };

  const handleColorChange = (component: string, property: string, color: string) => {
    state.setCustomColors(prev => ({
      ...prev,
      [component]: {
        ...prev[component],
        [property]: color,
      },
    }));
  };

  const handleSave = async () => {
    try {
      state.setIsSaving(true);
      state.setError(null);

      await themesApi.customize(state.selectedTheme, state.customColors);

      state.setShowSuccess(true);
      setTimeout(() => state.setShowSuccess(false), 3000);

      await loadThemes();
    } catch (error: any) {
      console.error('Failed to save theme:', error);
      state.setError(error.message || 'Failed to save theme customization');
    } finally {
      state.setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset this theme to its default colors?')) {
      return;
    }

    try {
      state.setIsResetting(true);
      state.setError(null);

      await themesApi.reset(state.selectedTheme);

      state.setShowResetSuccess(true);
      setTimeout(() => state.setShowResetSuccess(false), 3000);

      await loadThemeDetails();
    } catch (error: any) {
      console.error('Failed to reset theme:', error);
      state.setError(error.message || 'Failed to reset theme');
    } finally {
      state.setIsResetting(false);
    }
  };

  const handleExport = async () => {
    try {
      const exportData = await themesApi.export();
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'theme-customizations.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to export themes:', error);
      state.setError(error.message || 'Failed to export theme customizations');
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      await themesApi.import(data);

      state.setShowSuccess(true);
      setTimeout(() => state.setShowSuccess(false), 3000);

      await loadThemes();
      await loadThemeDetails();
    } catch (error: any) {
      console.error('Failed to import themes:', error);
      state.setError(error.message || 'Failed to import theme customizations');
    }

    event.target.value = '';
  };

  const copyThemeConfig = () => {
    const config = JSON.stringify(state.customColors, null, 2);
    navigator.clipboard.writeText(config);
    state.setCopied(true);
    setTimeout(() => state.setCopied(false), 2000);
  };

  return {
    loadThemes,
    loadThemeDetails,
    handleColorChange,
    handleSave,
    handleReset,
    handleExport,
    handleImport,
    copyThemeConfig,
  };
};
